import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { updatePublicPrivateVideoViewCount, updateVideoWatchCountApiCall } from "@/services/api";
import { useAuthToken } from "@/hooks/useAppSelectors";
import { RootState } from "@/store";
import { setVideoWatchedById } from "@/store/slices/video/seriesSlice";
import { setVideoWatchedFlag } from "@/store/slices/video/videoSlice";

import "./style.css";

type WistiaPlayerProps = {
    isWelcomeSeries: boolean;
    playNextVideo?: () => void;
    containerClassName?: string;
    title?: string;
};

let wistiaScriptLoadingPromise: Promise<void> | null = null;
const WISTIA_UNMUTED_PREF_KEY = "wistia_pref_unmuted";

const ensureWistiaScript = (): Promise<void> => {
    if ((window as any).Wistia && (window as any)._wq) {
        return Promise.resolve();
    }

    if (!wistiaScriptLoadingPromise) {
        wistiaScriptLoadingPromise = new Promise((resolve, reject) => {
            const existingScript = document.querySelector<HTMLScriptElement>(
                'script[src="https://fast.wistia.com/assets/external/E-v1.js"]'
            );

            if (existingScript) {
                if ((window as any).Wistia && (window as any)._wq) {
                    resolve();
                    return;
                }

                existingScript.addEventListener("load", () => resolve(), { once: true });
                existingScript.addEventListener("error", () => reject(new Error("Failed to load Wistia script")), { once: true });
                return;
            }

            const script = document.createElement("script");
            script.src = "https://fast.wistia.com/assets/external/E-v1.js";
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("Failed to load Wistia script"));
            document.body.appendChild(script);
        });
    }

    return wistiaScriptLoadingPromise;
};

const extractWistiaMediaId = (value: unknown): string => {
    if (typeof value !== "string" || !value.trim()) {
        return "";
    }

    const raw = value.trim();
    const idOnlyRegex = /^[a-zA-Z0-9]+$/;
    if (idOnlyRegex.test(raw)) {
        return raw;
    }

    try {
        const parsed = new URL(raw, window.location.origin);
        const segments = parsed.pathname.split("/").filter(Boolean);
        const mediaId = segments[segments.length - 1] || "";
        return mediaId.replace(/[^a-zA-Z0-9]/g, "");
    } catch {
        const cleaned = raw.split("?")[0].split("/").filter(Boolean).pop() || "";
        return cleaned.replace(/[^a-zA-Z0-9]/g, "");
    }
};

export default function WistiaPlayer(props: WistiaPlayerProps) {
    const { isWelcomeSeries, playNextVideo, containerClassName, title } = props;
    const dispatch = useDispatch();
    const token = useAuthToken();
    const [searchParams] = useSearchParams();

    const videoDetails = useSelector((state: RootState) => state.videoReducer.video);

    const seriesId = searchParams.get("series");
    const isSubscribedSeries = searchParams.get("subscription") === "true";

    const hasTriggeredWatchCount = useRef(false);
    const hasRiggeredViewCount = useRef(false);

    const shouldStartMuted = useMemo(() => {
        try {
            return localStorage.getItem(WISTIA_UNMUTED_PREF_KEY) !== "1";
        } catch {
            return true;
        }
    }, []);

    const wistiaMediaId = useMemo(() => {
        return extractWistiaMediaId(videoDetails?.videoId);
    }, [videoDetails?.videoId]);

    useEffect(() => {
        hasTriggeredWatchCount.current = false;
        hasRiggeredViewCount.current = false;
    }, [videoDetails?._id, videoDetails?.videoId]);

    const handleWatchCountUpdate = useCallback(
        async (seconds: number, duration: number) => {
            if (!token || hasTriggeredWatchCount.current) {
                return;
            }

            const secondsLeft = duration - seconds;
            const twoMinutes = 2 * 60;
            const nearEnd =
                duration > 0 &&
                (duration > twoMinutes
                    ? secondsLeft <= twoMinutes
                    : secondsLeft <= 15);

            if (!nearEnd) {
                return;
            }

            hasTriggeredWatchCount.current = true;

            const payload = {
                seriesId,
                videoDocId: videoDetails?._id,
                isWelcomeSeries,
                isSubscribedSeries,
            };

            const updateVideoWatchCountApiCallStatus = await updateVideoWatchCountApiCall(payload);
            if (updateVideoWatchCountApiCallStatus.status) {
                const currentVideoDocId = videoDetails?._id;
                if (currentVideoDocId) {
                    dispatch(
                        setVideoWatchedById({
                            videoDocId: currentVideoDocId,
                            isWatched: true,
                        })
                    );
                    dispatch(setVideoWatchedFlag(true));
                }
            } else {
                hasTriggeredWatchCount.current = false;
                console.log("issue in handleWatchCountUpdate");
            }
        },
        [dispatch, isSubscribedSeries, isWelcomeSeries, seriesId, token, videoDetails?._id]
    );

    const handleUpdatePublicPrivateVideoViewCount = useCallback(async () => {
        if (hasRiggeredViewCount.current || !videoDetails?.videoId) {
            return;
        }

        const isPublic = token ? false : true;
        const payload = {
            videoDocId: videoDetails?._id,
            seriesId,
            isWelcomeSeries,
            isWelcomeOfferVideo: Boolean(isWelcomeSeries),
            isSubscribedVideo: isSubscribedSeries,
        };

        hasRiggeredViewCount.current = true;
        const updateVideoWatchCountApiCallStatus = await updatePublicPrivateVideoViewCount(payload, isPublic);
        if (!updateVideoWatchCountApiCallStatus.status) {
            console.log("issue in handleUpdatePublicPrivateVideoViewCount");
        }
    }, [isSubscribedSeries, isWelcomeSeries, seriesId, token, videoDetails?._id, videoDetails?.videoId]);

    useEffect(() => {
        if (!wistiaMediaId) {
            return;
        }

        let isDisposed = false;
        let wistiaVideo: any = null;

        const bindEvents = async () => {
            try {
                await ensureWistiaScript();
                if (isDisposed) {
                    return;
                }

                (window as any)._wq = (window as any)._wq || [];
                (window as any)._wq.push({
                    id: wistiaMediaId,
                    options: {
                        autoPlay: true,
                        autoplay: true,
                            muted: shouldStartMuted,
                        playsinline: true,
                        videoFoam: true,
                        seo: true,
                    },
                    onReady: (video: any) => {
                        if (isDisposed) {
                            return;
                        }

                        wistiaVideo = video;

                        const handlePlay = () => {
                            handleUpdatePublicPrivateVideoViewCount();
                        };

                        const handleSecondChange = (second: number) => {
                            const duration = Number(video.duration?.() || 0);
                            handleWatchCountUpdate(Number(second || 0), duration);
                        };

                        const handleEnd = () => {
                            playNextVideo?.();
                        };

                        video.bind("play", handlePlay);
                        video.bind("secondchange", handleSecondChange);
                        video.bind("end", handleEnd);
                            video.bind("mutechange", (isMuted: boolean) => {
                                if (!isMuted) {
                                    localStorage.setItem(WISTIA_UNMUTED_PREF_KEY, "1");
                                }
                            });
                            video.bind("volumechange", (volume: number) => {
                                if (Number(volume) > 0) {
                                    localStorage.setItem(WISTIA_UNMUTED_PREF_KEY, "1");
                                }
                            });

                            if (!shouldStartMuted) {
                                video.unmute?.();
                            }

                        video.play?.().catch(() => {
                            // Ignore autoplay blocking errors by browser policies.
                        });
                    },
                });
            } catch (error) {
                console.error("Failed to initialize Wistia player:", error);
            }
        };

        bindEvents();

        return () => {
            isDisposed = true;
            if (wistiaVideo && typeof wistiaVideo.unbind === "function") {
                wistiaVideo.unbind("play");
                wistiaVideo.unbind("secondchange");
                wistiaVideo.unbind("end");
            }
        };
    }, [handleUpdatePublicPrivateVideoViewCount, handleWatchCountUpdate, playNextVideo, shouldStartMuted, wistiaMediaId]);

    if (!wistiaMediaId) {
        return (
            <div className={`${containerClassName || ""} wistiaPlayerRoot`}>
                <div className="wistiaPlayerFallback">Video unavailable</div>
            </div>
        );
    }

    return (
        <div className={`${containerClassName || ""} wistiaPlayerRoot mt-[20px] min-[551px]:mt-0`}>
            <div
                className={`wistia_embed wistia_async_${wistiaMediaId} wistiaPlayerEmbed`}
                aria-label={title || "Wistia video"}
            />
        </div>
    );
}
